import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

// Layer 2 (spark review) validation for imported / in-review-edited person-detail values. Mirrors the
// genesis Layer-1 `ImportedProfileSanitizer` caps so the review flags anything the server would reject
// or that a user edit pushes out of range. Kept in sync BY HAND — the client can't import the Java
// constants; changing a cap in `ImportedProfileSanitizer` means changing it here too.
export const IMPORT_FIELD_MAX_CHARS: Record<string, number> = {
  bio: 65535,
  profession: 128,
  jobTitle: 128,
  currentEmployer: 128,
  street: 128,
  phone: 32,
  email: 512,
  zipCode: 16,
};

export type ImportFieldError = "too-long" | "invalid-email" | "invalid-phone" | "child-count-max";

const EMAIL = z.email();

// person_detail.child_count has no DB upper bound; the profile editor caps it at 20
// (ProfilePage `childCountOverMax`), so the import review mirrors that same ceiling.
export const CHILD_COUNT_MAX = 20;

/**
 * The L2 error for a single person-detail field value, or null when it's fine. A blank value is never
 * an error (an empty value isn't committed). Length is checked for every capped field; `email` must be
 * a valid address; `phone` must be a valid number *when it carries a country code* — a national-format
 * number (no `+`) can't be validated without a region, so it's left alone, matching L1's fail-open;
 * `childCount` may not exceed {@link CHILD_COUNT_MAX} (mirrors the profile editor's guard).
 */
export function importFieldError(key: string, value: string | null | undefined): ImportFieldError | null {
  if (value == null || value.trim() === "") return null;
  const cap = IMPORT_FIELD_MAX_CHARS[key];
  if (cap !== undefined && value.length > cap) return "too-long";
  const trimmed = value.trim();
  if (key === "email" && !EMAIL.safeParse(trimmed).success) return "invalid-email";
  if (key === "phone" && trimmed.startsWith("+") && !isValidPhoneNumber(trimmed)) return "invalid-phone";
  if (key === "childCount" && Number.parseInt(trimmed, 10) > CHILD_COUNT_MAX) return "child-count-max";
  return null;
}
